import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { toast } from '@/components/ui/toast';
import {
  AlertTriangle,
  Loader2,
  Clapperboard,
  Inbox,
  RefreshCw,
  Pencil,
  Check,
  X,
  Plus,
  Trash2,
  Clock,
  Wand2,
} from 'lucide-react';
import type { EpisodeRecord, Scene, Dialogue } from './scriptExport';

interface BootstrapResp {
  ssmlDoc: { meta: { episodes?: EpisodeRecord[]; episodeCount?: number; mode?: string | null } | null };
}

interface PreviewResult {
  episodeIndex: number;
  title: string;
  summary: string;
  charCount: number;
  sceneCount: number;
  durationSec: number;
  changes: string[];
  draft: { scenes?: Scene[] } & Record<string, unknown>;
  original: Record<string, unknown>;
}

interface Props {
  episodeId: string;
  onFinish: () => void;
  /** 第一人称硬切分题材:隐藏 AI 润色面板,仅保留场景查看。 */
  hideRefine?: boolean;
}

interface SceneEditState {
  action: string;
  dialogues: Dialogue[];
}

function sceneVisibleLen(scene: Scene): number {
  const actionLen = (scene.action || '').replace(/\s/g, '').length;
  const dialogueLen = (scene.dialogues ?? []).reduce((sum, d) => sum + (d.text || '').replace(/\s/g, '').length, 0);
  const propLen = (scene.props ?? []).join('').replace(/\s/g, '').length;
  return actionLen + dialogueLen + Math.round(propLen * 0.35);
}

function deriveDensityIssues(episode: EpisodeRecord): string[] {
  const issues: string[] = [];
  (episode.scenes ?? []).forEach((scene, idx) => {
    const dur = (scene.cameras ?? []).reduce((sum, cam) => sum + (cam.durationSec ?? 0), 0) || (scene.durationSec ?? 0);
    const dialogues = scene.dialogues ?? [];
    const actionItems = dialogues.filter((d) => d.type === 'action');
    const speechItems = dialogues.filter((d) => d.type === 'speech' || d.type === 'inner_monologue' || !d.type);
    const visibleLen = sceneVisibleLen(scene);
    const propCount = (scene.props ?? []).filter((p) => typeof p === 'string' && p.trim()).length;
    const sceneId = scene.sceneId || `S${idx + 1}`;

    if (propCount === 0 && dur >= 10) issues.push(`${sceneId}:缺少关键道具/空间锚点`);
    if (dur >= 12 && dialogues.length < 5) issues.push(`${sceneId}:节拍条数不足(${dialogues.length}条/${dur.toFixed(0)}s)`);
    if (dur >= 12 && actionItems.length < 3) issues.push(`${sceneId}:动作节拍不足(${actionItems.length}条/${dur.toFixed(0)}s)`);
    if (dur >= 14 && visibleLen < 105) issues.push(`${sceneId}:内容密度不足(visible=${visibleLen}, dur=${dur.toFixed(0)}s)`);
    if (dur >= 14 && speechItems.reduce((sum, d) => sum + (d.text || '').replace(/\s/g, '').length, 0) < 18 && actionItems.reduce((sum, d) => sum + (d.text || '').replace(/\s/g, '').length, 0) < 65) {
      issues.push(`${sceneId}:15秒片段缺少台词/动作支撑`);
    }
  });
  return issues.slice(0, 12);
}

function extractThinSceneIds(issues: string[]): string[] {
  const ids = new Set<string>();
  issues.forEach((item) => {
    const match = item.match(/S\d+/);
    if (match) ids.add(match[0]);
  });
  return [...ids];
}

function buildThickenPrompt(episode: EpisodeRecord, issues: string[], sceneIds: string[]): string {
  const issueHint = issues.slice(0, 6).join('；');
  const episodeTitle = episode.title || `第 ${episode.index} 集`;
  const targetScenes = sceneIds.join('、');
  return `检测到${episodeTitle}的 S 场景偏薄，请按精品剧标准补厚以下场景：${targetScenes}。`
    + `要求：保持这些场景的 sceneId、场景顺序和核心冲突不变，只加厚内容——`
    + `优先补动作反馈、人物反应、关键道具结果、环境音和尾帧钩子；`
    + `每个被补厚的 S 的 dialogues 至少 6 条（含 ≥3 条 action 镜头动作 + ≥2 条 speech/OS），单 S 不超过 15 秒。`
    + `未被指定的场景保持原样不动。重点问题：${issueHint}`;
}

function SceneCard({
  sc,
  episodeId,
  episodeIndex,
  onSaved,
  deletable = false,
  onDeleted,
  thin = false,
}: {
  sc: Scene;
  episodeId: string;
  episodeIndex: number;
  onSaved: () => void;
  deletable?: boolean;
  onDeleted?: () => void;
  thin?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [draft, setDraft] = useState<SceneEditState>({ action: '', dialogues: [] });

  const removeScene = async () => {
    if (deleting) return;
    if (!window.confirm(`确定删除场景 ${sc.sceneId}？删除后其余场景会重新编号。`)) return;
    setDeleting(true);
    try {
      await api
        .delete(`writer/episodes/${episodeId}/scripts/${episodeIndex}/scenes/${sc.sceneId}`)
        .json();
      toast.success(`${sc.sceneId} 已删除`);
      onDeleted?.();
    } catch {
      toast.error('删除失败', '请稍后重试');
      setDeleting(false);
    }
  };

  const startEdit = () => {
    setDraft({
      action: sc.action ?? '',
      dialogues: (sc.dialogues ?? []).map((d) => ({ ...d })),
    });
    setEditing(true);
  };

  const cancelEdit = () => setEditing(false);

  const save = async () => {
    setSaving(true);
    try {
      await api
        .patch(
          `writer/episodes/${episodeId}/scripts/${episodeIndex}/scenes/${sc.sceneId}`,
          { json: { action: draft.action, dialogues: draft.dialogues } },
        )
        .json();
      toast.success(`${sc.sceneId} 已保存`);
      setEditing(false);
      onSaved();
    } catch {
      toast.error('保存失败', '请稍后重试');
    } finally {
      setSaving(false);
    }
  };

  const setDialogueText = (i: number, text: string) => {
    setDraft((prev) => {
      const next = prev.dialogues.map((d, idx) => (idx === i ? { ...d, text } : d));
      return { ...prev, dialogues: next };
    });
  };

  // 时长优先取 cameras 之和;无 cameras(如冷感生存日志的硬切分场景)时回退到场景自带 durationSec。
  const cameraDuration = (sc.cameras ?? []).reduce((sum, c) => sum + (c.durationSec ?? 0), 0);
  const sceneDuration = cameraDuration > 0 ? cameraDuration : (sc.durationSec ?? 0);

  return (
    <article className={`rounded-md border px-3 py-2 shadow-sm ${thin ? 'border-amber-200 bg-amber-50/25' : 'border-outline-variant/30 bg-surface'}`}>
      <header className="flex items-center justify-between text-xs text-outline">
        <div className="flex items-center gap-2">
          <span className="rounded bg-brand-50 px-1.5 py-0.5 text-brand-700">{sc.sceneId}</span>
          {sc.location && <span className="text-neutral-500">{sc.location}</span>}
          {thin && <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] text-amber-700">偏薄</span>}
          {sceneDuration > 0 && (
            <span className="flex items-center gap-0.5 text-[10px] text-neutral-400" title="基于内容估算的视频预估时长">
              <Clock className="h-2.5 w-2.5" />
              预估 {sceneDuration.toFixed(1)}s
            </span>
          )}
          {(sc as any).manuallyEdited && (
            <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[10px] text-amber-600">已手动编辑</span>
          )}
        </div>
        {!editing ? (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={startEdit}
              className="flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] text-neutral-400 transition hover:bg-brand-50 hover:text-brand-600"
              title="手动编辑此场景"
            >
              <Pencil className="h-3 w-3" />
              编辑
            </button>
            {deletable && (
              <button
                type="button"
                onClick={removeScene}
                disabled={deleting}
                className="flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] text-neutral-400 transition hover:bg-rose-50 hover:text-rose-600 disabled:opacity-50"
                title="删除此场景"
              >
                <Trash2 className="h-3 w-3" />
                {deleting ? '删除中…' : '删除'}
              </button>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="flex items-center gap-1 rounded bg-brand-500 px-2 py-0.5 text-[11px] text-white transition hover:bg-brand-600 disabled:opacity-60"
            >
              <Check className="h-3 w-3" />
              {saving ? '保存中…' : '保存'}
            </button>
            <button
              type="button"
              onClick={cancelEdit}
              disabled={saving}
              className="flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] text-neutral-500 transition hover:bg-neutral-100 disabled:opacity-60"
            >
              <X className="h-3 w-3" />
              取消
            </button>
          </div>
        )}
      </header>

      {editing ? (
        <textarea
          value={draft.action}
          onChange={(e) => setDraft((prev) => ({ ...prev, action: e.target.value }))}
          placeholder="场景动作描述（可留空）"
          rows={2}
          className="mt-1.5 w-full resize-none rounded border border-outline-variant/40 bg-neutral-50 px-2 py-1 text-xs leading-relaxed text-neutral-600 outline-none focus:border-brand-400 focus:bg-white"
        />
      ) : (
        sc.action && (
          <p className="mt-1 whitespace-pre-line text-xs leading-relaxed text-neutral-500">
            {sc.action}
          </p>
        )
      )}

      {!editing && (sc.props ?? []).length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {(sc.props ?? []).map((prop, idx) => (
            <span
              key={`${sc.sceneId}-prop-${idx}`}
              className="rounded-full bg-violet-50 px-2 py-0.5 text-[10px] text-violet-700"
            >
              关键道具 · {prop}
            </span>
          ))}
        </div>
      )}

      {editing ? (
        <>
          <ul className="mt-2 space-y-1.5">
            {draft.dialogues.map((d, i) => {
            const emo = (d.emotion || '').toLowerCase();
            const isInner =
              d.type === 'inner_monologue' ||
              emo.includes('内心') ||
              emo.includes('独白') ||
              emo === 'os';
            const isAction = d.type === 'action';
            return (
              <li key={i} className="flex items-start gap-1.5">
                <span
                  className={`mt-1 shrink-0 text-[11px] font-semibold ${isInner ? 'text-violet-600' : isAction ? 'text-neutral-400 italic' : 'text-brand-700'}`}
                >
                  {d.speaker}
                  {isInner ? ' · OS' : ''}:
                </span>
                <textarea
                  value={d.text}
                  onChange={(e) => setDialogueText(i, e.target.value)}
                  rows={2}
                  className={`flex-1 resize-none rounded border px-2 py-1 text-sm leading-relaxed outline-none focus:border-brand-400 focus:bg-white ${
                    isInner
                      ? 'border-violet-200 bg-violet-50/60 text-violet-900 italic focus:border-violet-400'
                      : isAction
                        ? 'border-neutral-200 bg-neutral-50 text-neutral-600 italic'
                        : 'border-outline-variant/40 bg-neutral-50 text-neutral-800'
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setDraft(prev => ({ ...prev, dialogues: prev.dialogues.filter((_, idx) => idx !== i) }))}
                  className="mt-1 shrink-0 rounded p-0.5 text-neutral-300 hover:bg-red-50 hover:text-red-500 transition"
                  title="删除此条"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </li>
            );
          })}
        </ul>
        <button
          type="button"
          onClick={() => setDraft(prev => ({
            ...prev,
            dialogues: [...prev.dialogues, { speaker: '', text: '', type: 'speech' as const }]
          }))}
          className="mt-1.5 flex w-full items-center gap-1 rounded border border-dashed border-outline-variant/50 px-2 py-1 text-xs text-neutral-400 hover:border-brand-400 hover:text-brand-500 transition"
        >
          <Plus className="h-3 w-3" /> 新增台词
        </button>
        </>
      ) : (
        (sc.dialogues ?? []).length > 0 && (
          <ul className="mt-1.5 space-y-1">
            {(sc.dialogues ?? []).map((d, i) => {
              const emo = (d.emotion || '').toLowerCase();
              const innerEmo =
                emo.includes('内心') ||
                emo.includes('独白') ||
                emo.includes('自言') ||
                emo === 'os' ||
                emo.includes('心声');
              const isInner = d.type === 'inner_monologue' || innerEmo;
              const isAction =
                d.type === 'action' ||
                (typeof d.text === 'string' && /^\s*[((].*[))]\s*$/.test(d.text));
              if (isAction) {
                return (
                  <li key={i} className="text-xs italic leading-relaxed text-neutral-500">
                    <span className="mr-1.5 text-neutral-400">·</span>
                    <span className="text-neutral-500">{d.speaker}</span>
                    <span className="ml-1">{d.text}</span>
                  </li>
                );
              }
              return (
                <li
                  key={i}
                  className={
                    isInner
                      ? 'rounded-md border-l-2 border-violet-300 bg-violet-50/60 px-2 py-1 text-sm italic leading-relaxed text-violet-900'
                      : 'text-sm leading-relaxed text-neutral-800'
                  }
                >
                  <strong className={isInner ? 'mr-1.5 text-violet-700' : 'mr-1.5 text-brand-700'}>
                    {d.speaker}
                    {isInner ? ' · OS' : ''}:
                  </strong>
                  {d.emotion && (
                    <span
                      className={
                        isInner ? 'mr-1 text-[10px] text-violet-500' : 'mr-1 text-[10px] text-neutral-400'
                      }
                    >
                      [{d.emotion}]
                    </span>
                  )}
                  {d.text}
                </li>
              );
            })}
          </ul>
        )
      )}
    </article>
  );
}

export function DialogueWorkspace({ episodeId, onFinish, hideRefine = false }: Props) {
  const [activeIdx, setActiveIdx] = useState<number | null>(null);
  type RefinePhase = 'idle' | 'previewing' | 'preview_done' | 'committing';
  const [refinePhase, setRefinePhase] = useState<RefinePhase>('idle');
  const [previewResult, setPreviewResult] = useState<PreviewResult | null>(null);
  const [thickenModalOpen, setThickenModalOpen] = useState(false);
  const [selectedThinIds, setSelectedThinIds] = useState<string[]>([]);
  const [thickenTargetIds, setThickenTargetIds] = useState<string[]>([]);
  const [repairingRhythm, setRepairingRhythm] = useState(false);

  const ep = useQuery({
    queryKey: ['episode', episodeId],
    queryFn: () => api.get(`writer/episodes/${episodeId}`).json<BootstrapResp>(),
    enabled: Boolean(episodeId),
  });

  const episodes: EpisodeRecord[] = useMemo(() => {
    const list = ep.data?.ssmlDoc?.meta?.episodes;
    if (!Array.isArray(list)) return [];
    return [...list].sort((a, b) => (a.index || 0) - (b.index || 0));
  }, [ep.data]);

  useEffect(() => {
    if (activeIdx === null && episodes.length > 0) {
      setActiveIdx(episodes[0].index);
    }
  }, [activeIdx, episodes]);

  const active = episodes.find((e) => e.index === activeIdx) ?? null;
  const scriptMode = ep.data?.ssmlDoc?.meta?.mode ?? null;
  const densityIssues = useMemo(() => {
    if (!active) return [];
    if (scriptMode === 'premium' && Array.isArray(active.shotReadinessIssues) && active.shotReadinessIssues.length > 0) {
      return active.shotReadinessIssues;
    }
    if (scriptMode === 'premium' && Array.isArray(active.densityIssues) && active.densityIssues.length > 0) return active.densityIssues;
    if (scriptMode !== 'premium' && Array.isArray(active.rhythmIssues) && active.rhythmIssues.length > 0) return active.rhythmIssues;
    if (scriptMode === 'premium') return deriveDensityIssues(active);
    return [];
  }, [active, scriptMode]);
  const thinSceneIds = useMemo(() => extractThinSceneIds(densityIssues), [densityIssues]);
  const episodeDurationSec = (active?.scenes ?? []).reduce((sum, s) => {
    const cam = (s.cameras ?? []).reduce((a, c) => a + (c.durationSec ?? 0), 0);
    return sum + (cam > 0 ? cam : (s.durationSec ?? 0));
  }, 0);

  const startPreview = async (overridePrompt: string, targetSceneIds: string[] = []) => {
    const nextPrompt = overridePrompt.trim();
    if (!active || !nextPrompt || refinePhase !== 'idle') return;
    setRefinePhase('previewing');
    try {
      const result = await api
        .post(`writer/episodes/${episodeId}/scripts/${active.index}/refine/preview`, {
          json: { prompt: nextPrompt },
        })
        .json<PreviewResult>();
      
      // 补厚场景合并：只保留targetSceneIds指定场景的新版本，其他场景用原数据
      if (targetSceneIds.length > 0 && active.scenes) {
        const originalScenes = active.scenes;
        const draftScenes = result.draft?.scenes || [];
        const mergedScenes = originalScenes.map((origScene) => {
          if (!targetSceneIds.includes(origScene.sceneId)) {
            return origScene;
          }
          const newScene = draftScenes.find((s) => s.sceneId === origScene.sceneId);
          return newScene || origScene;
        });
        result.draft = { ...result.draft, scenes: mergedScenes };
        result.sceneCount = mergedScenes.length;
        
        // 重新计算总字数和总时长（基于合并后的场景）
        let totalChars = 0;
        let totalDur = 0;
        mergedScenes.forEach((sc) => {
          (sc.dialogues || []).forEach((d) => {
            if (d.text) totalChars += d.text.length;
          });
          const camDur = (sc.cameras || []).reduce((s, c) => s + (c.durationSec || 0), 0);
          totalDur += camDur > 0 ? camDur : (sc.durationSec || 0);
        });
        result.charCount = totalChars;
        result.durationSec = Math.round(totalDur * 10) / 10;
      }
      
      setPreviewResult(result);
      setRefinePhase('preview_done');
    } catch (e) {
      setRefinePhase('idle');
      setThickenTargetIds([]);
      toast.error('补厚预览失败', (e as Error).message || '网络异常');
    }
  };

  const acceptPreview = async () => {
    if (!previewResult || refinePhase !== 'preview_done') return;
    setRefinePhase('committing');
    try {
      await api
        .post(`writer/episodes/${episodeId}/scripts/${previewResult.episodeIndex}/refine/commit`, {
          json: { draft: previewResult.draft },
        })
        .json();
      toast.success(`第 ${previewResult.episodeIndex} 集已保存`, `${previewResult.charCount} 字 · ${previewResult.sceneCount} 场`);
      setPreviewResult(null);
      setRefinePhase('idle');
      setThickenTargetIds([]);
      ep.refetch();
      onFinish();
    } catch (e) {
      setRefinePhase('preview_done');
      toast.error('保存失败', (e as Error).message || '网络异常');
    }
  };

  const discardPreview = () => {
    setPreviewResult(null);
    setRefinePhase('idle');
    setThickenTargetIds([]);
  };

  const openThickenModal = () => {
    if (!active || densityIssues.length === 0 || refinePhase !== 'idle') return;
    setSelectedThinIds(thinSceneIds);
    setThickenModalOpen(true);
  };

  const toggleThinId = (sceneId: string) => {
    setSelectedThinIds((prev) =>
      prev.includes(sceneId) ? prev.filter((id) => id !== sceneId) : [...prev, sceneId],
    );
  };

  const confirmThicken = async () => {
    if (!active || selectedThinIds.length === 0) return;
    setThickenTargetIds(selectedThinIds);
    const relatedIssues = densityIssues.filter((issue) => {
      const match = issue.match(/S\d+/);
      return match ? selectedThinIds.includes(match[0]) : false;
    });
    const nextPrompt = buildThickenPrompt(active, relatedIssues, selectedThinIds);
    await startPreview(nextPrompt, selectedThinIds);
    setThickenModalOpen(false);
  };

  const repairRhythm = async () => {
    if (!active || scriptMode === 'premium' || repairingRhythm) return;
    setRepairingRhythm(true);
    try {
      const result = await api
        .post(`writer/episodes/${episodeId}/scripts/${active.index}/rhythm/repair`)
        .json<{ sceneCount: number; durationSec: number; rhythmIssues?: string[] }>();
      toast.success(
        `第 ${active.index} 集已按红果节奏重切`,
        `${result.sceneCount} 个 S · 预估 ${result.durationSec.toFixed(1)}s`
          + ((result.rhythmIssues?.length ?? 0) > 0 ? ` · 仍有 ${result.rhythmIssues?.length} 项需人工确认` : ''),
      );
      await ep.refetch();
      onFinish();
    } catch (e) {
      toast.error('节奏重切失败', (e as Error).message || '网络异常');
    } finally {
      setRepairingRhythm(false);
    }
  };

  if (ep.isLoading) {
    return <div className="h-72 animate-pulse rounded-lg bg-surface-variant" />;
  }

  if (episodes.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-outline-variant/40 bg-surface-variant/30 px-4 py-12 text-center">
        <Inbox className="h-6 w-6 text-outline" />
        <p className="text-sm text-neutral-600">尚未生成分集脚本</p>
        <p className="text-xs text-neutral-400">请先回到「热点驱动」启动编剧 Agent 生成完整分集脚本</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="grid h-[calc(100vh-16rem)] gap-3 md:grid-cols-[14rem_1fr]">
      {/* 左侧:集数列表 */}
      <aside className="flex min-h-0 flex-col rounded-lg border border-outline-variant/30 bg-surface-variant/30">
        <div className="flex shrink-0 items-center justify-between border-b border-outline-variant/30 px-3 py-2 text-xs text-outline">
          <span>共 {episodes.length} 集</span>
          <button
            type="button"
            onClick={() => ep.refetch()}
            className="text-neutral-500 transition hover:text-brand-600"
            title="刷新"
          >
            <RefreshCw className="h-3 w-3" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {episodes.map((e) => {
            const isActive = e.index === activeIdx;
            return (
              <button
                key={e.index}
                onClick={() => setActiveIdx(e.index)}
                className={
                  isActive
                    ? 'mb-1 flex w-full items-center gap-2 rounded-md bg-brand-500 px-2.5 py-1.5 text-left text-xs text-white shadow-sm'
                    : 'mb-1 flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-xs text-neutral-700 transition hover:bg-surface'
                }
              >
                <Clapperboard className="h-3 w-3 shrink-0" />
                <span className="line-clamp-1 flex-1 font-medium">
                  {e.title || `第 ${e.index} 集`}
                </span>
                {typeof e.charCount === 'number' && (
                  <span
                    className={
                      isActive ? 'text-[10px] text-white/80' : 'text-[10px] text-neutral-400'
                    }
                  >
                    {e.charCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </aside>

      {/* 右侧:脚本预览 + 场景编辑 + 对话润色 */}
      <section className="flex min-h-0 flex-col gap-3">
        {active ? (
          <>
            <div className="rounded-lg border border-outline-variant/30 bg-surface px-4 py-3">
              <div className="flex items-center gap-2">
                <h3 className="text-base font-semibold text-neutral-900">{active.title || `第 ${active.index} 集`}</h3>
                <span className="rounded-full bg-brand-50 px-2 py-0.5 text-[10px] text-brand-700">
                  第 {active.index} 集
                </span>
                {typeof active.charCount === 'number' && (
                  <span className="text-[11px] text-neutral-500">{active.charCount} 字</span>
                )}
                {(active.scenes ?? []).length > 0 && (
                  <span className="text-[11px] text-neutral-500">{(active.scenes ?? []).length} 个场景 S</span>
                )}
                {episodeDurationSec > 0 && (
                  <span className="flex items-center gap-0.5 text-[11px] text-brand-600" title="本集所有场景的视频预估总时长">
                    <Clock className="h-3 w-3" />
                    预估 {episodeDurationSec.toFixed(1)}s
                  </span>
                )}
              </div>
              {active.summary && (
                <p className="mt-1 line-clamp-3 text-xs leading-relaxed text-neutral-600">
                  {active.summary}
                </p>
              )}
            </div>

            <div className="min-h-0 flex-1 space-y-2 overflow-y-auto rounded-lg border border-outline-variant/30 bg-surface-variant/30 p-3 text-sm">
              {densityIssues.length > 0 && (
                <div className="flex items-start justify-between gap-3 rounded-md border border-amber-200 bg-amber-50/60 px-3 py-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 text-xs font-medium text-amber-800">
                      <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                      {scriptMode === 'premium' ? '检测到可拍性问题' : '检测到节奏问题'}
                    </div>
                    <p className="mt-0.5 line-clamp-2 text-[11px] leading-relaxed text-amber-700">
                      {densityIssues.slice(0, 2).join(' · ')}
                      {densityIssues.length > 2 ? ' …' : ''}
                    </p>
                  </div>
                  {!hideRefine && scriptMode === 'premium' && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={openThickenModal}
                      disabled={refinePhase !== 'idle'}
                      className="shrink-0 border-amber-200 text-amber-800 hover:bg-amber-100"
                    >
                      <Wand2 className="h-3.5 w-3.5" />
                      一键补厚
                    </Button>
                  )}
                  {!hideRefine && scriptMode !== 'premium' && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={repairRhythm}
                      disabled={refinePhase !== 'idle' || repairingRhythm}
                      className="shrink-0 border-amber-200 text-amber-800 hover:bg-amber-100"
                    >
                      {repairingRhythm ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <RefreshCw className="h-3.5 w-3.5" />
                      )}
                      按节奏重切
                    </Button>
                  )}
                </div>
              )}
              {(active.scenes ?? []).map((sc) => (
                <SceneCard
                  key={sc.sceneId}
                  sc={sc}
                  episodeId={episodeId}
                  episodeIndex={active.index}
                  onSaved={() => ep.refetch()}
                  deletable={hideRefine}
                  onDeleted={() => ep.refetch()}
                  thin={thinSceneIds.includes(sc.sceneId)}
                />
              ))}
              {(active.scenes ?? []).length === 0 && (
                <div className="px-2 py-4 text-center text-xs text-neutral-500">本集尚未拆出场景</div>
              )}
            </div>

            {!hideRefine && (refinePhase === 'preview_done' || refinePhase === 'committing') && previewResult && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
                <div className="flex h-[90vh] w-full max-w-5xl flex-col rounded-lg border border-outline bg-surface shadow-xl">
                  <div className="flex items-center justify-between border-b border-outline-variant px-6 py-4">
                    <div>
                      <h3 className="text-lg font-medium text-brand-700">
                        补厚预览 · 第 {previewResult.episodeIndex} 集
                      </h3>
                      <p className="mt-1 text-sm text-on-surface-variant">
                        {previewResult.sceneCount} 场景 · {previewResult.charCount} 字 · {previewResult.durationSec}秒
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={discardPreview}
                      disabled={refinePhase === 'committing'}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="flex-1 overflow-y-auto px-6 py-4">
                    {previewResult.changes && previewResult.changes.length > 0 && (
                      <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-4">
                        <div className="mb-2 text-sm font-medium text-amber-900">变更摘要</div>
                        <ul className="space-y-1 text-xs text-amber-800">
                          {previewResult.changes.map((c, i) => (
                            <li key={i} className="leading-relaxed">· {c}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <div className="space-y-3">
                      <div className="text-sm font-medium text-on-surface">
                        补厚后的场景预览（{thickenTargetIds.length} 个场景）
                      </div>
                      {(previewResult.draft?.scenes || [])
                        .filter((sc: Scene) => thickenTargetIds.length === 0 || thickenTargetIds.includes(sc.sceneId))
                        .map((sc: Scene) => {
                          const camDur = (sc.cameras || []).reduce((s, c) => s + (c.durationSec || 0), 0);
                          const sceneDur = camDur > 0 ? camDur : (sc.durationSec || 0);
                          return (
                            <div key={sc.sceneId} className="rounded-lg border border-brand-200 bg-brand-50/20 p-4">
                              <div className="mb-2 flex items-center gap-2">
                                <span className="rounded bg-brand-100 px-2 py-0.5 text-xs font-medium text-brand-700">
                                  {sc.sceneId}
                                </span>
                                <span className="text-xs text-on-surface-variant">{sc.location}</span>
                                <span className="ml-auto flex items-center gap-0.5 text-xs text-brand-600">
                                  <Clock className="h-3 w-3" />
                                  {sceneDur > 0 ? `${sceneDur.toFixed(1)}秒` : '时长缺失'}
                                </span>
                              </div>
                              <div className="space-y-1 text-xs text-on-surface">
                                {(sc.dialogues || []).map((d, idx) => (
                                  <div key={idx} className={d.type === 'action' ? 'text-neutral-600 italic' : ''}>
                                    {(d.type === 'speech' || d.type === 'inner_monologue') && (
                                      <strong className="mr-1 text-brand-700">{d.speaker}:</strong>
                                    )}
                                    {d.text}
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                  <div className="flex items-center justify-end gap-3 border-t border-outline-variant px-6 py-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={discardPreview}
                      disabled={refinePhase === 'committing'}
                    >
                      放弃
                    </Button>
                    <Button
                      variant="agent"
                      agentColor="writer"
                      size="sm"
                      onClick={acceptPreview}
                      disabled={refinePhase === 'committing'}
                    >
                      {refinePhase === 'committing' ? (
                        <>
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          保存中…
                        </>
                      ) : (
                        '确认应用 · 覆盖原场景'
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* 补厚弹窗 */}
            {thickenModalOpen && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
                <div className="mx-4 w-full max-w-2xl rounded-lg border border-outline bg-surface p-6 shadow-lg">
                  <h3 className="mb-4 text-lg font-medium text-on-surface">
                    检测到偏薄的 S 场景（{thinSceneIds.length} 个）
                  </h3>
                  <p className="mb-4 text-sm text-on-surface-variant">
                    勾选需要补厚的场景，将优先补动作节拍、人物反应、道具结果和尾帧钩子。
                  </p>
                  <div className="mb-4 max-h-64 space-y-2 overflow-y-auto rounded border border-outline-variant p-3">
                    {thinSceneIds.map((scId) => {
                      const scene = active?.scenes?.find((s) => s.sceneId === scId);
                      const checked = selectedThinIds.includes(scId);
                      return (
                        <label
                          key={scId}
                          className="flex cursor-pointer items-start gap-3 rounded p-2 hover:bg-surface-variant/30"
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleThinId(scId)}
                            className="mt-0.5 h-4 w-4 accent-brand-600"
                          />
                          <div className="flex-1 text-sm">
                            <div className="font-medium text-on-surface">{scId}</div>
                            {scene?.location && (
                              <div className="text-xs text-on-surface-variant">{scene.location}</div>
                            )}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedThinIds(selectedThinIds.length === thinSceneIds.length ? [] : thinSceneIds)}
                    >
                      {selectedThinIds.length === thinSceneIds.length ? '取消全选' : '全选'}
                    </Button>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={() => setThickenModalOpen(false)}>
                        取消
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={confirmThicken}
                        disabled={selectedThinIds.length === 0 || refinePhase !== 'idle'}
                      >
                        {refinePhase === 'previewing' ? (
                          <>
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            补厚生成中…
                          </>
                        ) : (
                          `开始补厚 (${selectedThinIds.length})`
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="flex h-full items-center justify-center rounded-lg border border-dashed border-outline-variant/40 px-6 py-12 text-sm text-neutral-500">
            选择左侧一集开始微调
          </div>
        )}
      </section>
      </div>
    </div>
  );
}
